import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';

import VehicleAcceptanceFormDialog from './components/VehicleAcceptanceFormDialog.jsx';
import VehicleAcceptanceTable from './components/VehicleAcceptanceTable.jsx';
import VehicleAcceptanceFilters from './components/VehicleAcceptanceFilters.jsx';
import VehicleAcceptanceHistoryDialog from './components/VehicleAcceptanceHistoryDialog.jsx';
import DocumentDownloadHeader from '../shared/components/DocumentDownloadHeader.jsx';
import { useEditVehicleAcceptanceForm } from './hooks/useUpdateVehicleAcceptanceForm.js';
import { useCreateVehicleAcceptanceForm } from './hooks/useCreateVehicleAcceptanceForm.js';
import { useVehicleAcceptanceFormList } from './hooks/useVehicleAcceptanceFormList.js';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLazyExportVehicleAcceptanceToExcelQuery } from '../../../../api/endpoints/vehicleAcceptenceForm.js';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton.jsx';
import { VAFStatus } from './constants/vafStatus.js';

const TAB_STATUS_MAP = {
    0: VAFStatus.DRAFT, // Taslak
    1: VAFStatus.SENT_FOR_APPROVAL, // Onay Bekleyenler
    2: VAFStatus.RETURNED, // İade Edilenler
    3: VAFStatus.APPROVED // Onaylananlar
};

const VehicleAcceptanceForm = () => {
    const { th } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(0);
    const currentStatus = TAB_STATUS_MAP[activeIndex];

    const vafFileKey = '3f77cc58-b5f6-4942-9654-766a133aa42c';

    const [navigationData, setNavigationData] = useState(null);

    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentHistoryRecord, setCurrentHistoryRecord] = useState(null);

    const { lazyParams, items, totalRecords, tabCounts, loading, refetch, onPage, onSort, filterFields, handleSearch, handleApplyFilters, handleResetFilters, queryParams } = useVehicleAcceptanceFormList(th, currentStatus);

    const customFilterTypes = useMemo(
        () => ({
            startDate: 'date',
            endDate: 'date',
            vehicleAcceptanceStartDate: 'date',
            vehicleAcceptanceEndDate: 'date'
        }),
        []
    );

    useEffect(() => {
        refetch();
    }, [refetch]);

    const {
        updateDialogVisible,
        selectedVehicleAcceptanceForm,
        editFormData,
        onEditInputChange,
        onEditNumberChange,
        onEditDateChange,
        onEditDropdownChange,
        updatedFiles,
        onEditFilesChange,
        onRemoveFile,
        submitted: editSubmitted,
        handleEdit,
        hideUpdateDialog,
        handleUpdate,
        isUpdating,
        onSendForApproval,
        isSendingForApproval,
        onReturn,
        isReturning,
        onApprove,
        isApproving,
        onDelete,
        isDeleting,
        documents,
        isLoadingDocuments,
        onDeleteDocument,
        onDownloadDocument,
        onUpdateDocument,
        isDownloadingDocument,
        isDeletingDocument,
        isUpdatingDocument,
        isWaitingForDocumentConfirmation,
        toast,
        setToastCallback,
        hasDocumentOperations,
        hasFormDataChanged
    } = useEditVehicleAcceptanceForm(refetch);

    useEffect(() => {
        const storedNavigation = localStorage.getItem('crossModuleNavigation');
        if (storedNavigation) {
            try {
                const parsedData = JSON.parse(storedNavigation);
                if (parsedData.targetModule === 'VAF' && parsedData.shouldNavigate) {
                    setNavigationData(parsedData);
                    localStorage.removeItem('crossModuleNavigation');
                }
            } catch (error) {
                localStorage.removeItem('crossModuleNavigation');
            }
        }
    }, []);

    useEffect(() => {
        if (navigationData && navigationData.chassisNo && handleEdit) {
            // 1. Onaylananlar tabına geç (index 3)
            setActiveIndex(3);

            // 2. Şasi numarası ile filtreleme yap
            setTimeout(() => {
                // Şasi numarası filter field'ını set et
                if (filterFields.chassisNo) {
                    filterFields.chassisNo.value = navigationData.chassisNo;
                    handleApplyFilters();
                }
            }, 100);
        }
    }, [navigationData, handleEdit, filterFields, handleApplyFilters]);

    useEffect(() => {
        if (navigationData && navigationData.triggeredFrom === 'warranty-claims' && navigationData.targetModule === 'VAF' && navigationData.chassisNo && items && items.length > 0 && handleEdit && !updateDialogVisible) {
            setTimeout(() => {
                handleEdit(items[0]);

                setTimeout(() => {
                    setNavigationData(null);

                    const remainingData = localStorage.getItem('crossModuleNavigation');
                    if (remainingData) {
                        localStorage.removeItem('crossModuleNavigation');
                    }
                }, 500);
            }, 200);
        }
    }, [navigationData, items, handleEdit, updateDialogVisible]);

    useEffect(() => {
        if (navigationData && navigationData.triggeredFrom === 'warranty-claims' && navigationData.targetModule === 'VAF' && navigationData.chassisNo && items && items.length === 0 && !loading) {
            setTimeout(() => {
                setNavigationData(null);

                const remainingData = localStorage.getItem('crossModuleNavigation');
                if (remainingData) {
                    localStorage.removeItem('crossModuleNavigation');
                }
            }, 1000);
        }
    }, [navigationData, items, loading]);

    const { createDialogVisible, createFormData, onCreateInputChange, onCreateNumberChange, onCreateDateChange, onCreateDropdownChange, createSubmitted, isCreating, openNew, hideCreateDialog, handleCreate } = useCreateVehicleAcceptanceForm(refetch);

    const handleTabChange = (e) => {
        setActiveIndex(e.index);
    };

    const handleShowHistory = (record) => {
        setCurrentHistoryRecord(record);
        setHistoryDialogVisible(true);
    };

    const hideHistoryDialog = () => {
        setHistoryDialogVisible(false);
        setCurrentHistoryRecord(null);
    };

    const tabLabels = [
        { label: th('warranty.vehicleAcceptanceForm.tabs.draft'), icon: 'pi pi-file-edit', color: 'orange' },
        { label: th('warranty.vehicleAcceptanceForm.tabs.pendingApproval'), icon: 'pi pi-clock', color: 'blue' },
        { label: th('warranty.vehicleAcceptanceForm.tabs.returned'), icon: 'pi pi-replay', color: 'red' },
        { label: th('warranty.vehicleAcceptanceForm.tabs.approved'), icon: 'pi pi-check-circle', color: 'green' }
    ];

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="page-header mb-4">
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <h1 className="page-title mb-2">{th('warranty.vehicleAcceptanceForm.title')}</h1>
                    </div>
                    <div className="flex gap-2">
                        <GenericFileExportButton
                            useExportQuery={useLazyExportVehicleAcceptanceToExcelQuery}
                            exportParams={queryParams}
                            buttonLabel={th('common.export')}
                            buttonClassName="p-button-success"
                            icon="pi pi-file-excel"
                            defaultFileName="AracKabulFormu"
                        />
                        <Button label={th('warranty.vehicleAcceptanceForm.addNew')} icon="pi pi-plus" onClick={openNew} />
                    </div>
                </div>
            </div>

            <div className="flex justify-content-between align-items-center mb-3">
                <DocumentDownloadHeader
                    fileKey={vafFileKey}
                    documentLabelKey="warranty.vehicleAcceptanceForm.download.vafDocument"
                    downloadingMessageKey="warranty.vehicleAcceptanceForm.download.downloading"
                    successMessageKey="warranty.vehicleAcceptanceForm.download.success"
                    errorMessageKey="warranty.vehicleAcceptanceForm.download.error"
                />
            </div>

            <VehicleAcceptanceFilters filterFields={filterFields} handleSearch={handleSearch} handleApplyFilters={handleApplyFilters} handleResetFilters={handleResetFilters} customFilterTypes={customFilterTypes} />

            <TabView activeIndex={activeIndex} onTabChange={handleTabChange} className="vaf-tabs">
                {tabLabels.map((tab, index) => (
                    <TabPanel
                        key={index}
                        header={
                            <span className="tab-header">
                                <i className={`${tab.icon} mr-2`}></i>
                                {tab.label}
                                {tabCounts[TAB_STATUS_MAP[index]] > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{tabCounts[TAB_STATUS_MAP[index]]}</span>}
                            </span>
                        }
                        className="tab-content"
                    >
                        <VehicleAcceptanceTable
                            items={items}
                            lazyParams={lazyParams}
                            totalRecords={totalRecords}
                            loading={loading}
                            onPage={onPage}
                            onSort={onSort}
                            handleEdit={handleEdit}
                            handleShowHistory={handleShowHistory}
                            tabLabel={tab.label}
                            currentStatus={TAB_STATUS_MAP[index]}
                            th={th}
                        />
                    </TabPanel>
                ))}
            </TabView>

            <VehicleAcceptanceFormDialog
                visible={updateDialogVisible}
                onHide={hideUpdateDialog}
                formData={editFormData}
                recordId={selectedVehicleAcceptanceForm?.id}
                onInputChange={onEditInputChange}
                onNumberChange={onEditNumberChange}
                onDateChange={onEditDateChange}
                onDropdownChange={onEditDropdownChange}
                onSubmit={handleUpdate}
                formSubmitted={editSubmitted}
                isSubmitting={isUpdating}
                isEditMode={true}
                updatedFiles={updatedFiles}
                onEditFilesChange={onEditFilesChange}
                onRemoveFile={onRemoveFile}
                onSendForApproval={onSendForApproval}
                isSendingForApproval={isSendingForApproval}
                onReturn={onReturn}
                isReturning={isReturning}
                onApprove={onApprove}
                isApproving={isApproving}
                onDelete={onDelete}
                isDeleting={isDeleting}
                documents={documents}
                isLoadingDocuments={isLoadingDocuments}
                onDeleteDocument={onDeleteDocument}
                onDownloadDocument={onDownloadDocument}
                onUpdateDocument={onUpdateDocument}
                isDownloadingDocument={isDownloadingDocument}
                isDeletingDocument={isDeletingDocument}
                isUpdatingDocument={isUpdatingDocument}
                isWaitingForDocumentConfirmation={isWaitingForDocumentConfirmation}
                toastRef={toast}
                setToastCallback={setToastCallback}
                hasDocumentOperations={hasDocumentOperations}
                hasFormDataChanged={hasFormDataChanged}
                th={th}
            />

            <VehicleAcceptanceFormDialog
                visible={createDialogVisible}
                onHide={hideCreateDialog}
                formData={createFormData}
                onInputChange={onCreateInputChange}
                onNumberChange={onCreateNumberChange}
                onDateChange={onCreateDateChange}
                onDropdownChange={onCreateDropdownChange}
                onSubmit={handleCreate}
                formSubmitted={createSubmitted}
                isSubmitting={isCreating}
                isEditMode={false}
                th={th}
            />

            <VehicleAcceptanceHistoryDialog dialogVisible={historyDialogVisible} hideDialog={hideHistoryDialog} vehicleAcceptanceForm={currentHistoryRecord} />
        </div>
    );
};

export default VehicleAcceptanceForm;
